import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  Alert,
  Fade,
  styled
} from '@mui/material';
import {
  AccountBalance as GrantIcon,
  Event as EventIcon,
  Receipt as ExpenseIcon,
  Transform as TransferIcon,
  School as ScholarshipIcon,
  Favorite as HardshipIcon,
  Work as ContractorIcon,
  AttachMoney as ReimbursementIcon,
  CalendarToday as BudgetIcon
} from '@mui/icons-material';

import {
  fetchVendors,
  fetchProjects,
  fetchEmployees,
  createReimbursementRequest,
  createContractorPayment,
  createExpenseRequest,
} from '../../../services/api/apiService';

import ReimbursementRequestForm from './forms/ReimbursementRequestForm';
import ContractorPaymentForm from './forms/ContractorPaymentForm';
import ExpenseRequestFormPro from './forms/ExpenseRequestFormPro';
import HardshipGrantForm from './forms/HardshipGrantForm';
import ScholarshipGrantApplicationForm from './forms/ScholarshipGrantApplicationForm';
import GrantCreationForm from './forms/GrantCreationForm';
import FundProgramForm from './forms/FundProgramForm';
import TransferFundsForm from './forms/TransferFundsForm';
import EventBudgetForm from './forms/EventBudgetForm';

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(18, 18, 18, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px',
  padding: theme.spacing(3),
  color: '#fff',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #00BCD4 0%, #4CAF50 100%)',
    borderRadius: '12px 12px 0 0',
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    color: '#fff',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#00BCD4',
  },
  '& .MuiSvgIcon-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  }
}));

const StyledProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '& .MuiLinearProgress-bar': {
    background: 'linear-gradient(90deg, #00BCD4 0%, #4CAF50 100%)',
    borderRadius: 4,
  }
}));

const requestTypes = [
  { value: 'reimbursement', label: 'Reimbursement Request', icon: <ReimbursementIcon /> },
  { value: 'contractorPayment', label: 'Contractor/Vendor Payment', icon: <ContractorIcon /> },
  { value: 'expenseRequest', label: 'Expense Request', icon: <ExpenseIcon /> },
  { value: 'hardshipGrant', label: 'Hardship Grant', icon: <HardshipIcon /> },
  { value: 'scholarshipGrant', label: 'Scholarship', icon: <ScholarshipIcon /> },
  { value: 'createGrant', label: 'Create Grant', icon: <GrantIcon /> },
  { value: 'fundEvent', label: 'Fund Event', icon: <EventIcon /> },
  { value: 'transferFunds', label: 'Transfer Funds', icon: <TransferIcon /> },
  { value: 'eventBudget', label: 'Event Budget', icon: <BudgetIcon /> }
];

const UnifiedRequestForm = () => {
  // State declarations
  const [requestType, setRequestType] = useState('');
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [progress, setProgress] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [vendorsData, projectsData, employeesData] = await Promise.all([
          fetchVendors(),
          fetchProjects(),
          fetchEmployees()
        ]);
        setVendors(vendorsData);
        setProjects(projectsData);
        setEmployees(employeesData);
      } catch (err) {
        setError('Failed to load initial data');
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, []);

  const renderFormSection = () => {
    const commonProps = {
      vendors,
      projects,
      employees,
      onSubmit: handleSubmit,
      isSubmitting
    };

    switch (requestType) {
      case 'reimbursement':
        return <ReimbursementRequestForm {...commonProps} />;
      
      case 'contractorPayment':
        return <ContractorPaymentForm {...commonProps} />;
      
      case 'expenseRequest':
        return <ExpenseRequestFormPro {...commonProps} />;
      
      case 'hardshipGrant':
        return <HardshipGrantForm {...commonProps} />;
      
      case 'scholarshipGrant':
        return <ScholarshipGrantApplicationForm {...commonProps} />;
      
      case 'createGrant':
        return <GrantCreationForm {...commonProps} />;
      
      case 'fundEvent':
        return <FundProgramForm {...commonProps} />;
      
      case 'transferFunds':
        return <TransferFundsForm {...commonProps} />;
      
      case 'eventBudget':
        return <EventBudgetForm {...commonProps} />;
      
      default:
        return null;
    }
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      let response;
      switch (requestType) {
        case 'reimbursement':
          response = await createReimbursementRequest(formData);
          break;
        case 'contractorPayment':
          response = await createContractorPayment(formData);
          break;
        case 'expenseRequest':
          response = await createExpenseRequest(formData);
          break;
        default:
          throw new Error('Unknown request type');
      }
      
      if (response.success) {
        setProgress(prev => Math.min(prev + 10, 100));
        if (progress >= 90) {
          setRewards(prev => [...prev, `${requestType} Request Completed!`]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to submit request');
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <StyledPaper elevation={3}>
        <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExpenseIcon sx={{ color: '#00BCD4' }} />
          Submit a Request
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 4 }}>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Select Request Type
          </InputLabel>
          <StyledSelect
            value={requestType}
            onChange={(e) => setRequestType(e.target.value)}
            label="Select Request Type"
          >
            <MenuItem value="" disabled>Select an option</MenuItem>
            {requestTypes.map((type) => (
              <MenuItem 
                key={type.value} 
                value={type.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {type.icon}
                {type.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Submission Progress
            </Typography>
            <Typography variant="body2" sx={{ color: '#00BCD4' }}>
              {progress}%
            </Typography>
          </Box>
          <StyledProgress variant="determinate" value={progress} />
        </Box>

        {renderFormSection()}

        {rewards.length > 0 && (
          <Fade in={true}>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#4CAF50' }}>
                Your Rewards
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {rewards.map((reward, index) => (
                  <Chip
                    key={index}
                    label={reward}
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: '#4CAF50',
                      borderRadius: '16px'
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Fade>
        )}

        {isSubmitting && (
          <Alert 
            severity="info"
            sx={{
              mt: 2,
              backgroundColor: 'rgba(0, 188, 212, 0.1)',
              color: '#00BCD4',
              '& .MuiAlert-icon': {
                color: '#00BCD4'
              }
            }}
          >
            Processing your request...
          </Alert>
        )}
      </StyledPaper>
    </Box>
  );
};

export default UnifiedRequestForm; 